import { Button, Stack } from "@mui/material";
import { useAtomValue } from "jotai";
import { type FC, useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useDismissPortfolioAlertMutation } from "web_myplann/Api/Portfolio/useDismissPortfolioAlertMutation";
import { usePortfolioAlertBoxesQuery } from "web_myplann/Api/Portfolio/usePortfolioAlertBoxesQuery";
import {
	type AlertBoxDismissModeEnum,
	type ClientPortfolioDomainEnum,
	type PortfolioAlertBoxDTO,
	type PortfolioDetailResponseDTO,
	PortfolioEventTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { EventAlertBox } from "web_myplann/NotificationCenter/EventAlert/EventAlertBox";
import {
	getEventVariant,
	resolveClassification,
} from "web_myplann/NotificationCenter/EventAlert/eventClassification";
import { getEventUrgencyScore } from "web_myplann/NotificationCenter/EventAlert/eventTiming";
import {
	type EventClassificationEnum,
	EventSourceEnum,
} from "web_myplann/NotificationCenter/EventAlert/types";
import { useEventActions } from "web_myplann/NotificationCenter/EventAlert/useEventActions";
import {
	EVENT_DATE_PARAM,
	EVENT_SOURCE_PARAM,
	EVENT_TYPE_PARAM,
} from "web_myplann/NotificationCenter/Utils/contractEventParams";
import { ContactAgentDialog } from "web_myplann/Shared/Components/ContactAgentDialog";
import { rightsState } from "web_myplann/Shared/State/rightsAtom";
import { parseEnumValue } from "web_myplann/Shared/Utils/parseEnumValue";

const MAX_VISIBLE = 3;

const DEEP_LINK_PARAMS = [
	EVENT_SOURCE_PARAM,
	EVENT_TYPE_PARAM,
	EVENT_DATE_PARAM,
] as const;

/**
 * One-shot handover from the Notification Center. `bypassDismissal` ignores
 * dismissal records; `eventType` + `eventDate` additionally resolve that one
 * event even when it falls outside the alert-box visibility window.
 */
type DeepLinkContext = {
	bypassDismissal: boolean;
	eventType?: PortfolioEventTypeEnum;
	eventDate?: string;
};

const NO_DEEP_LINK: DeepLinkContext = { bypassDismissal: false };

type ResolvedAlert = {
	alert: PortfolioAlertBoxDTO;
	classification: EventClassificationEnum;
};

type Props = {
	contractId: number;
	domain?: ClientPortfolioDomainEnum | null;
	portfolio?: PortfolioDetailResponseDTO | null;
};

export const EventAlertStack: FC<Props> = ({
	contractId,
	domain,
	portfolio,
}) => {
	const { t } = useResource();
	const rights = useAtomValue(rightsState);
	const pilotEnabled = rights?.pilotNotificationCenter ?? false;

	const [searchParams, setSearchParams] = useSearchParams();
	const location = useLocation();
	const [expanded, setExpanded] = useState(false);

	// A tap from the Notification Center bypasses server-side dismissal and
	// targets one specific event, on the initial load only; once the user
	// dismisses again the normal rules apply. The params are read into state
	// before the effect below strips them from the URL, so this is one-shot
	// session state, not a persistent URL flag.
	const [deepLink, setDeepLink] = useState<DeepLinkContext>(() =>
		searchParams.get(EVENT_SOURCE_PARAM) === EventSourceEnum.NotificationCenter
			? {
					bypassDismissal: true,
					eventType: parseEnumValue(
						PortfolioEventTypeEnum,
						searchParams.get(EVENT_TYPE_PARAM),
					),
					eventDate: searchParams.get(EVENT_DATE_PARAM) ?? undefined,
				}
			: NO_DEEP_LINK,
	);

	useEffect(() => {
		if (!DEEP_LINK_PARAMS.some((param) => searchParams.has(param))) {
			return;
		}
		const next = new URLSearchParams(searchParams);
		for (const param of DEEP_LINK_PARAMS) {
			next.delete(param);
		}
		setSearchParams(next, { replace: true, state: location.state });
	}, [searchParams, setSearchParams, location.state]);

	const { data: alerts } = usePortfolioAlertBoxesQuery(
		contractId,
		deepLink.bypassDismissal,
		pilotEnabled,
		deepLink.eventType,
		deepLink.eventDate,
	);
	const { mutate: dismissAlert } = useDismissPortfolioAlertMutation();
	const { handleAction, contactDialogOpen, closeContactDialog, confirmLead } =
		useEventActions(contractId, portfolio);

	if (!pilotEnabled) {
		return null;
	}

	const today = new Date();

	const items: ResolvedAlert[] = (alerts ?? [])
		.map((alert) => ({
			alert,
			classification: resolveClassification(domain, alert.eventType, portfolio),
		}))
		.filter((item): item is ResolvedAlert => item.classification !== null)
		.sort((a, b) => {
			const urgencyDiff =
				getEventUrgencyScore(a.alert.eventDate, today) -
				getEventUrgencyScore(b.alert.eventDate, today);
			if (urgencyDiff !== 0) {
				return urgencyDiff;
			}
			const variantA = getEventVariant(a.alert.eventType);
			const variantB = getEventVariant(b.alert.eventType);
			if (variantA === variantB) {
				return 0;
			}
			return variantA === "warning" ? -1 : 1;
		});

	if (items.length === 0) {
		return null;
	}

	const shownItems = expanded ? items : items.slice(0, MAX_VISIBLE);
	const hiddenCount = items.length - shownItems.length;

	const onDismiss = (
		alert: PortfolioAlertBoxDTO,
		mode: AlertBoxDismissModeEnum,
	) => {
		dismissAlert({
			contractId,
			eventType: alert.eventType,
			eventDate: alert.eventDate,
			mode,
		});
		// Back to the standard rules: drop the whole deep-link context, not just
		// the dismissal bypass, otherwise the targeted event would stay pinned.
		setDeepLink(NO_DEEP_LINK);
	};

	return (
		<Stack gap={1.2} pb={2}>
			{shownItems.map(({ alert, classification }) => (
				<EventAlertBox
					key={`${alert.eventType}:${alert.eventDate}`}
					alert={alert}
					classification={classification}
					onAction={(action) => handleAction(action, alert)}
					onDismiss={(mode) => onDismiss(alert, mode)}
				/>
			))}

			{hiddenCount > 0 && (
				<Button
					variant="text"
					size="small"
					onClick={() => setExpanded(true)}
					sx={{ alignSelf: "flex-start" }}
				>
					{t(Resources.EventAlert.moreEvents, { count: hiddenCount })}
				</Button>
			)}

			<ContactAgentDialog
				open={contactDialogOpen}
				onClose={closeContactDialog}
				onConfirm={confirmLead}
				title={t(Resources.EventAlert.ContactDialog.title)}
				description={t(Resources.EventAlert.ContactDialog.description)}
				buttonLabel={t(Resources.EventAlert.ContactDialog.button)}
				successTitle={t(Resources.EventAlert.ContactDialog.successTitle)}
				successDescription={t(
					Resources.EventAlert.ContactDialog.successDescription,
				)}
			/>
		</Stack>
	);
};
